import { doc, getDoc, increment } from 'firebase/firestore';
import uniqid from 'uniqid';
import { Order, OrderLog, Wallet, WalletLog, Worker } from '../../apiTypes';
import TransactionCategories from '../../constants/transactionCategories';
import { db } from '../../firebase';
import { createBatch } from '../../utils/api';
import getDateKey from '../../utils/dates/getDateKey';
import getBankCommission from '../../utils/getBankCommission';

type Props = {
  cafeSlug: string;
  order: DbDocument<Order>;
  worker: DbDocument<Worker>;
  wallets: DbDocument<Wallet>[];
};

export const cancelOrderPaymentNew = async ({ order, worker, cafeSlug, wallets }: Props) => {
  if (order.paid && typeof order.paid?.byCard !== 'number' && typeof order.paid?.cash !== 'number') {
    const batch = createBatch();

    const reportDocRef = doc(db, 'cafes', cafeSlug, 'archivedOrders', `${getDateKey(order.paidTime)}`);
    const reportDocSnap = await getDoc(reportDocRef);

    if (reportDocSnap.exists()) {
      batch.delete(`cafes/${cafeSlug}/archivedOrders/${reportDocSnap.id}`);
    }

    Object.entries(order.paid.byCard).forEach(([legalEntityId, sum]) => {
      if (sum) {
        const wallet =
          wallets.find((w) => w.legalEntityId === legalEntityId && w.forCardPayments) ||
          wallets.find((w) => !w.legalEntityId && w.forCardPayments);

        const bankCommission = getBankCommission(sum, wallet?.bankPercent);

        if (wallet) {
          const log: WalletLog = {
            time: new Date().getTime(),
            sum: -sum,
            workerId: worker.id,
            workerName: worker.name,
            wallet: wallet.name,
            bankCommission: -bankCommission,
            category: order.type === 'delivery' ? TransactionCategories.delivery : TransactionCategories.in_house_sales,
            total: (wallet.balance || 0) - sum + bankCommission,
            info: { type: 'cancelOrder', id: order.id, number: order.number },
            type: 'cancelOrder',
            comment: '',
          };
          batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${wallet.id}`, {
            balance: increment(-sum + bankCommission),
          });
          batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${uniqid()}`, log);
        }
      }
    });

    Object.entries(order.paid.cash).forEach(([legalEntityId, sum]) => {
      if (sum) {
        const wallet =
          wallets.find((w) => w.legalEntityId === legalEntityId && w.forCashPayments) ||
          wallets.find((w) => !w.legalEntityId && w.forCashPayments);

        const bankCommission = getBankCommission(sum, wallet?.bankPercent);

        if (wallet) {
          const log: WalletLog = {
            time: new Date().getTime(),
            sum: -sum,
            workerId: worker.id,
            workerName: worker.name,
            wallet: wallet.name,
            bankCommission: -bankCommission,
            category: order.type === 'delivery' ? TransactionCategories.delivery : TransactionCategories.in_house_sales,
            total: (wallet.balance || 0) - sum + bankCommission,
            info: { type: 'cancelOrder', id: order.id, number: order.number },
            type: 'cancelOrder',
            comment: '',
          };
          batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${wallet.id}`, {
            balance: increment(-sum + bankCommission),
          });
          batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${uniqid()}`, log);
        }
      }
    });

    const orderLog: OrderLog = {
      time: Date.now(),
      workerId: worker?.id || '',
      workerName: worker?.name || 'Visitor',
      type: 'cancelPayment',
      state: {
        workerId: order.workerId,
        workerName: order.workerName,
        tableId: order.tableId,
        tableName: order.tableName,
        clientId: order.clientId || '',
        clientName: order.clientName || '',
        discount: order.discount || 0,
        items: order.items,
        status: order.status,
        paid: null,
        delivery: order.delivery || null,
      },
    };

    batch.update<Order>(`cafes/${cafeSlug}/orders/${order.id}`, {
      paid: null,
      prroReceipts: {},
      paidTime: 0,
      closed: false,
    });

    batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order.id}/logs/${uniqid()}`, orderLog);

    await batch.commit();
  }
};
